import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  VideoCamera,
  Calendar,
  Plus,
  X,
  Copy,
  Users as UsersIcon,
  ArrowRight,
  Trash,
  MagnifyingGlass,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const Meet = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [form, setForm] = useState({ title: "", scheduled_at: "", invitee_ids: [] });
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState("");

  const load = async () => {
    const r = await api.get("/meetings");
    setMeetings(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openSchedule = async () => {
    setShowSchedule(true);
    if (contacts.length === 0) {
      const r = await api.get("/users");
      setContacts(r.data);
    }
  };

  const closeSchedule = () => {
    setShowSchedule(false);
    setForm({ title: "", scheduled_at: "", invitee_ids: [] });
  };

  const toggleInvitee = (userId) => {
    setForm((f) => ({
      ...f,
      invitee_ids: f.invitee_ids.includes(userId)
        ? f.invitee_ids.filter((x) => x !== userId)
        : [...f.invitee_ids, userId],
    }));
  };

  const startInstant = async () => {
    try {
      const r = await api.post("/meetings", { title: "Instant meeting", scheduled_at: null, invitee_ids: [] });
      navigate(`/app/meet/room/${r.data.room_name}`);
    } catch {
      toast.error("Could not start meeting");
    }
  };

  const submitSchedule = async () => {
    if (!form.title.trim()) return toast.error("Add a title");
    setScheduling(true);
    try {
      const payload = { title: form.title, scheduled_at: form.scheduled_at || null, invitee_ids: form.invitee_ids };
      const r = await api.post("/meetings", payload);
      toast.success("MEETING CREATED");
      closeSchedule();
      setMeetings((m) => [r.data, ...m]);
    } catch {
      toast.error("Failed to schedule");
    } finally {
      setScheduling(false);
    }
  };

  const joinByCode = () => {
    const code = joinCode.trim().replace(/^.*\//, ""); // accept full URL too
    if (!code) return toast.error("Enter a meeting code");
    navigate(`/app/meet/room/${encodeURIComponent(code)}`);
  };

  const cancelMeeting = async (m) => {
    if (!confirm(`Cancel "${m.title}"?`)) return;
    await api.delete(`/meetings/${m.id}`);
    setMeetings((arr) => arr.filter((x) => x.id !== m.id));
    toast.success("CANCELLED");
  };

  const copyLink = (m) => {
    const url = `${window.location.origin}/app/meet/room/${m.room_name}`;
    navigator.clipboard.writeText(url);
    toast.success("LINK COPIED");
  };

  const filteredContacts = contacts.filter(
    (c) => !contactSearch || c.name.toLowerCase().includes(contactSearch.toLowerCase())
  );

  const now = new Date();
  const upcoming = meetings.filter((m) => !m.scheduled_at || new Date(m.scheduled_at) >= now);
  const past = meetings.filter((m) => m.scheduled_at && new Date(m.scheduled_at) < now);

  return (
    <div className="min-h-full">
      <header className="border-b border-line p-6 flex items-end justify-between">
        <div>
          <div className="label-tag text-ink-3">// 09 — MEETINGS</div>
          <h1 className="font-heading font-black text-4xl mt-1">MEETINGS</h1>
        </div>
        <button onClick={load} className="p-2 border border-line hover:bg-paper-tint" data-testid="meet-refresh">
          <ArrowClockwise size={16} />
        </button>
      </header>

      {/* Actions */}
      <section className="border-b border-line bg-paper-white">
        <div className="grid grid-cols-1 md:grid-cols-3">
          <button
            onClick={startInstant}
            className="border-r border-line p-8 text-left hover:bg-paper-tint transition-colors group"
            data-testid="start-instant-btn"
          >
            <VideoCamera size={32} weight="duotone" />
            <h3 className="font-heading font-bold text-xl mt-4">START INSTANT</h3>
            <p className="text-xs text-ink-2 mt-2">Launch a video room now. Share the link to invite anyone.</p>
            <div className="mt-4 text-xs uppercase tracking-widest font-semibold flex items-center gap-1">
              GO LIVE <ArrowRight size={12} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          <button
            onClick={openSchedule}
            className="border-r border-line p-8 text-left hover:bg-paper-tint transition-colors group"
            data-testid="schedule-btn"
          >
            <Calendar size={32} weight="duotone" />
            <h3 className="font-heading font-bold text-xl mt-4">SCHEDULE</h3>
            <p className="text-xs text-ink-2 mt-2">Plan ahead. Invite team members and set a time.</p>
            <div className="mt-4 text-xs uppercase tracking-widest font-semibold flex items-center gap-1">
              PLAN A CALL <ArrowRight size={12} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          <div className="p-8">
            <UsersIcon size={32} weight="duotone" />
            <h3 className="font-heading font-bold text-xl mt-4">JOIN BY CODE</h3>
            <p className="text-xs text-ink-2 mt-2">Paste a meeting code or full link.</p>
            <div className="mt-4 flex">
              <input
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && joinByCode()}
                placeholder="abc-xyz-123"
                className="flex-1 border border-line p-2 text-sm"
                data-testid="join-code-input"
              />
              <button onClick={joinByCode} className="btn-primary" data-testid="join-code-btn">JOIN</button>
            </div>
          </div>
        </div>
      </section>

      {/* Meetings list */}
      <section className="p-6 max-w-3xl">
        <div className="label-tag text-ink-3 mb-3">UPCOMING & INSTANT · {upcoming.length}</div>
        {loading ? (
          <div className="text-sm text-ink-3">LOADING…</div>
        ) : upcoming.length === 0 ? (
          <div className="brut-card p-8 text-center text-ink-3 text-sm" data-testid="empty-upcoming">
            <VideoCamera size={40} weight="duotone" className="mx-auto mb-3" />
            — No upcoming meetings. Start one above. —
          </div>
        ) : (
          <div className="brut-card divide-y divide-line">
            {upcoming.map((m) => (
              <MeetingRow key={m.id} m={m} me={user} onJoin={() => navigate(`/app/meet/room/${m.room_name}`)} onCopy={() => copyLink(m)} onCancel={() => cancelMeeting(m)} />
            ))}
          </div>
        )}

        {past.length > 0 && (
          <>
            <div className="label-tag text-ink-3 mt-8 mb-3">PAST · {past.length}</div>
            <div className="brut-card divide-y divide-line opacity-70">
              {past.map((m) => (
                <MeetingRow key={m.id} m={m} me={user} onJoin={() => navigate(`/app/meet/room/${m.room_name}`)} onCopy={() => copyLink(m)} onCancel={() => cancelMeeting(m)} isPast />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Schedule modal */}
      {showSchedule && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center p-4 z-50" onClick={closeSchedule}>
          <div className="brut-card max-w-lg w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="schedule-modal">
            <div className="border-b border-line p-4 flex items-center justify-between shrink-0">
              <h3 className="font-heading font-bold">SCHEDULE MEETING</h3>
              <button onClick={closeSchedule}><X size={18} /></button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              <div>
                <div className="label-tag mb-2">TITLE</div>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Weekly sync"
                  className="w-full border border-line p-3 text-sm focus:border-ink"
                  data-testid="meeting-title"
                />
              </div>
              <div>
                <div className="label-tag mb-2">WHEN (OPTIONAL)</div>
                <input
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                  className="w-full border border-line p-3 text-sm focus:border-ink"
                  data-testid="meeting-when"
                />
                <div className="text-xs text-ink-3 mt-1">Leave empty for an instant, on-demand room.</div>
              </div>
              <div>
                <div className="label-tag mb-2">INVITE PEOPLE · {form.invitee_ids.length} SELECTED</div>
                <div className="flex border border-line mb-2">
                  <span className="px-3 flex items-center text-ink-3"><MagnifyingGlass size={14} /></span>
                  <input
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    placeholder="Search…"
                    className="flex-1 p-2 text-sm"
                    data-testid="invite-search"
                  />
                </div>
                <div className="border border-line max-h-52 overflow-y-auto divide-y divide-line">
                  {filteredContacts.length === 0 && <div className="p-3 text-xs text-ink-3">— No members —</div>}
                  {filteredContacts.map((c) => {
                    const on = form.invitee_ids.includes(c.user_id);
                    return (
                      <button
                        key={c.user_id}
                        onClick={() => toggleInvitee(c.user_id)}
                        className={`w-full flex items-center gap-3 p-3 text-left brut-hover ${on ? "bg-paper-tint" : ""}`}
                        data-testid={`invitee-${c.user_id}`}
                      >
                        <img
                          src={c.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${c.user_id}`}
                          className="w-8 h-8 border border-line"
                          alt=""
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{c.name}</div>
                          <div className="text-xs text-ink-3 truncate">{c.headline || "Member"}</div>
                        </div>
                        <div className={`w-5 h-5 border ${on ? "bg-ink border-ink" : "border-line"}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
              <button onClick={submitSchedule} disabled={scheduling} className="btn-primary w-full" data-testid="schedule-submit">
                {scheduling ? "SAVING…" : "CREATE MEETING"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MeetingRow = ({ m, me, onJoin, onCopy, onCancel, isPast }) => {
  const isHost = m.host?.user_id === me?.user_id;
  return (
    <div className="p-4 flex items-center gap-3" data-testid={`meeting-${m.id}`}>
      <div className="w-10 h-10 border border-line bg-paper-tint flex items-center justify-center">
        <VideoCamera size={18} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{m.title}</div>
        <div className="text-xs text-ink-3 truncate">
          {m.scheduled_at ? new Date(m.scheduled_at).toLocaleString() : "Instant"} · Host: {m.host?.name || "—"}
          {m.participants?.length ? ` · ${m.participants.length} invited` : ""}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={onCopy} className="p-2 border border-line hover:bg-paper-tint" title="Copy link" data-testid={`copy-${m.id}`}>
          <Copy size={14} />
        </button>
        {isHost && (
          <button onClick={onCancel} className="p-2 border border-line hover:bg-paper-tint text-ink-2 hover:text-signal-red" title="Cancel" data-testid={`cancel-${m.id}`}>
            <Trash size={14} />
          </button>
        )}
        {!isPast && (
          <button onClick={onJoin} className="btn-primary text-xs" data-testid={`join-${m.id}`}>
            JOIN
          </button>
        )}
      </div>
    </div>
  );
};

export default Meet;
